import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { toast } from 'sonner';

import { Action } from '@/authorization/action';
import { Subject } from '@/authorization/subject';
import { useAppAbility } from '@/authorization/use-app-ability';
import { useUpdateProductionDeclaration } from '@/services/apis/mutations/production-declaration/use-update-production-declaration';
import type { ProductionDeclarationMaterial } from '@/types/core/purchase-order/production-declaration-material';
import { ProductionDeclarationMaterialStatusCode } from '@/types/core/purchase-order/production-declaration-material-status-code';
import { ProductionDeclarationStatusCode } from '@/types/core/purchase-order/production-declaration-status-code';

type ProductionDeclarationActionVisibility = {
  showSubmit: boolean;
  showApprove: boolean;
  showRefuse: boolean;
};

function getProductionDeclarationActionVisibility(input: {
  statusCode: string;
  isSupplier: boolean;
  canManage: boolean;
  materials: ProductionDeclarationMaterial[];
}): ProductionDeclarationActionVisibility {
  const { statusCode, isSupplier, canManage, materials } = input;

  const isPending = statusCode === ProductionDeclarationStatusCode.Pending;
  const isSubmitted = statusCode === ProductionDeclarationStatusCode.Submitted;

  const hasRejectedMaterial = materials.some(
    (material) =>
      material.status.code === ProductionDeclarationMaterialStatusCode.Rejected,
  );
  const allMaterialsApproved =
    materials.length > 0 &&
    materials.every(
      (material) =>
        material.status.code ===
        ProductionDeclarationMaterialStatusCode.Approved,
    );

  return {
    showSubmit: isSupplier && isPending,
    showApprove: canManage && isSubmitted && allMaterialsApproved,
    showRefuse: canManage && isSubmitted && hasRejectedMaterial,
  };
}

type UseProductionDeclarationActionParams = {
  statusCode: string;
  productionDeclarationId: string;
  token?: string;
  materials?: ProductionDeclarationMaterial[];
};

export type ProductionDeclarationActionCallbacks = {
  onSuccess?: () => void;
  recipients?: string[];
  message?: string;
};

export function useProductionDeclarationAction({
  statusCode,
  productionDeclarationId,
  token,
  materials = [],
}: UseProductionDeclarationActionParams) {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation('productionDeclaration');
  const { t: tCommon } = useTranslation('common');
  const ability = useAppAbility();
  const { mutate: updateProductionDeclaration, isPending } =
    useUpdateProductionDeclaration();

  const canManage = ability.can(Action.Manage, Subject.ProductionDeclaration);

  const visibility = useMemo(
    () =>
      getProductionDeclarationActionVisibility({
        statusCode,
        isSupplier: !isAuthenticated,
        canManage,
        materials,
      }),
    [statusCode, isAuthenticated, canManage, materials],
  );

  const hasAnyAction =
    visibility.showSubmit || visibility.showApprove || visibility.showRefuse;

  const handleSubmit = (callbacks?: ProductionDeclarationActionCallbacks) => {
    updateProductionDeclaration(
      {
        id: productionDeclarationId,
        token,
      },
      {
        onSuccess: () => {
          toast.success(t('submitSuccess'));
          callbacks?.onSuccess?.();
        },
        onError: () => {
          toast.error(tCommon('error'), {
            description: t('submitError'),
          });
        },
      },
    );
  };

  const handleApprove = (callbacks?: ProductionDeclarationActionCallbacks) => {
    updateProductionDeclaration(
      {
        id: productionDeclarationId,
        isApproved: true,
        recipients: callbacks?.recipients,
        message: callbacks?.message,
      },
      {
        onSuccess: () => {
          toast.success(t('approveSuccess'));
          callbacks?.onSuccess?.();
        },
        onError: () => {
          toast.error(tCommon('error'), {
            description: t('approveError'),
          });
        },
      },
    );
  };

  const handleRefuse = (callbacks?: ProductionDeclarationActionCallbacks) => {
    updateProductionDeclaration(
      {
        id: productionDeclarationId,
        isApproved: false,
        recipients: callbacks?.recipients,
        message: callbacks?.message,
      },
      {
        onSuccess: () => {
          toast.success(t('refuseSuccess'));
          callbacks?.onSuccess?.();
        },
        onError: () => {
          toast.error(tCommon('error'), {
            description: t('refuseError'),
          });
        },
      },
    );
  };

  return {
    visibility,
    hasAnyAction,
    handleSubmit,
    handleApprove,
    handleRefuse,
    isPending,
  };
}
